import { FINANCIAL_GROWTH_LINE_ITEM_PLACEMENTS } from '@/shared/components/financial-statements/revenueSegments';
import {
    balanceSheetLineItemOrdering,
    cashFlowLineItemOrdering,
    dividendsLineItemOrdering,
    incomeStatementLineItemOrdering,
    multiplesLineItemOrdering,
} from '@/shared/schemas';

import type { FinancialFormulaOperandToken } from '../../types';

export interface FinancialFormulaNavigationTarget {
    focusLineItem?: string;
    lineItem: string;
    lineItemCandidates?: readonly string[];
    tab: 'balance' | 'cashflows' | 'dividends' | 'income' | 'multiples';
}

const lineItemSets = {
    balance: new Set<string>(balanceSheetLineItemOrdering),
    cashflows: new Set<string>(cashFlowLineItemOrdering),
    dividends: new Set<string>(dividendsLineItemOrdering),
    income: new Set<string>(incomeStatementLineItemOrdering),
    multiples: new Set<string>(multiplesLineItemOrdering),
};

const operandAliases: Readonly<Record<string, FinancialFormulaNavigationTarget>> = {
    annualDividendsPerShare: { lineItem: 'amount', tab: 'dividends' },
    averageAssets: { lineItem: 'totalAssets', tab: 'balance' },
    averageEquity: { lineItem: 'totalStockholdersEquity', tab: 'balance' },
    cashAndEquivalents: { lineItem: 'cashAndCashEquivalents', tab: 'balance' },
    closingPayables: { lineItem: 'accountsPayable', tab: 'balance' },
    closingReceivables: { lineItem: 'netReceivables', tab: 'balance' },
    commonEquity: { lineItem: 'totalStockholdersEquity', tab: 'balance' },
    currentAssets: { lineItem: 'totalCurrentAssets', tab: 'balance' },
    currentLiabilities: { lineItem: 'totalCurrentLiabilities', tab: 'balance' },
    earningsPerShare: { lineItem: 'eps', lineItemCandidates: ['eps', 'epsDiluted', 'epsdiluted'], tab: 'income' },
    openingPayables: { lineItem: 'accountsPayable', tab: 'balance' },
    openingReceivables: { lineItem: 'netReceivables', tab: 'balance' },
    shareholdersEquity: { lineItem: 'totalStockholdersEquity', tab: 'balance' },
    sharesOutstanding: {
        lineItem: 'weightedAverageShsOut',
        lineItemCandidates: [
            'weightedAverageShsOut',
            'weightedAverageSharesOutstanding',
            'weightedAverageShares',
            'weightedAverageShsOutDil',
            'weightedAverageSharesOutstandingDiluted',
            'weightedAverageSharesDiluted',
        ],
        tab: 'income',
    },
};

export const resolveFinancialFormulaNavigationTarget = (
    operand: FinancialFormulaOperandToken,
): FinancialFormulaNavigationTarget | undefined => {
    const alias = operandAliases[operand.key];

    if (alias) {
        return alias;
    }

    const growthPlacement = FINANCIAL_GROWTH_LINE_ITEM_PLACEMENTS[operand.key];

    if (growthPlacement) {
        const tabBySection = {
            'balance-sheet': 'balance',
            'cash-flow': 'cashflows',
            dividends: 'dividends',
            'income-statement': 'income',
        } as const;

        return {
            focusLineItem: operand.key,
            lineItem: growthPlacement.parentLineItems[0],
            lineItemCandidates: growthPlacement.parentLineItems,
            tab: tabBySection[growthPlacement.section],
        };
    }

    const categoryTabs: Partial<
        Record<FinancialFormulaOperandToken['category'], FinancialFormulaNavigationTarget['tab']>
    > = {
        'balance-sheet': 'balance',
        'cash-flow': 'cashflows',
        'income-statement': 'income',
        valuation: 'multiples',
    };
    const tab = categoryTabs[operand.category];

    return tab && lineItemSets[tab].has(operand.key) ? { lineItem: operand.key, tab } : undefined;
};

const financialViewPath = (pathname: string, tab: FinancialFormulaNavigationTarget['tab']) => {
    const companyOrFundMatch = pathname.match(/^(\/search\/(?:companies|funds)\/[^/]+)/);

    if (companyOrFundMatch) {
        return `${companyOrFundMatch[1]}/${tab}`;
    }

    const investmentMatch = pathname.match(/^(\/investments\/[^/]+)/);

    return investmentMatch ? `${investmentMatch[1]}/analysis/${tab}` : undefined;
};

export const createFinancialFormulaOperandHref = (
    pathname: string,
    search: string,
    operand: FinancialFormulaOperandToken,
) => {
    const target = resolveFinancialFormulaNavigationTarget(operand);
    const targetPath = target ? financialViewPath(pathname, target.tab) : undefined;

    if (!target || !targetPath) {
        return undefined;
    }

    const query = new URLSearchParams(search);

    query.set('lineItems', target.lineItem);
    query.set('focusLineItem', target.focusLineItem ?? target.lineItem);

    if (target.lineItemCandidates?.length) {
        query.set('lineItemCandidates', target.lineItemCandidates.join(','));
    } else {
        query.delete('lineItemCandidates');
    }

    return `${targetPath}?${query.toString()}`;
};
